/* global Config */

import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
// eslint-disable-next-line import/no-extraneous-dependencies
import { v4 as uuidv4 } from 'uuid';
import { isURL } from 'validator';

import Button from '../core/button';
import Error from '../core/error';
import './uploader.scss';

export default function ImageUploader({
                                        autoUpload = false,
                                        currentImage,
                                        formData,
                                        onChange,
                                      }) {
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState();
  const [uploaded, setUploaded] = useState(false);

  // reset when category changes
  useEffect(() => {
    setFile(null);
    setPreview(null);
    setUploading(false);
    setError(undefined);
    setUploaded(false);
  }, [formData?.values?.shellImageCategory]);

  // perform the multipart POST
  const uploadFile = async f => {
    setUploading(true);
    setError(undefined);

    const body = new FormData();
    body.append('uuid', uuidv4());
    body.append('filename', f.name);
    body.append('totalFileSize', f.size.toString());
    body.append('image', f);

    try {
      const res = await fetch(Config.base.imageUploadUrl, {
        method: 'POST',
        credentials: 'include',              // send cookies just like FineUploaderTraditional did
        headers: {
          'X-Requested-With': 'XMLHttpRequest'  // mirror Fine Uploader’s default XHR header
        },
        body,
      });

      const text = await res.text();
      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }

      const uploadedUrl = text.trim();
      if (!isURL(uploadedUrl)) {
        throw new Error('Server did not return a valid URL');
      }

      onChange(uploadedUrl, f.name);
      setUploaded(true);
    } catch (err) {
      setError(`Upload error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  // drop handler
  const onDrop = useCallback(
    (accepted, rejected) => {
      if (rejected.length) {
        setError('Invalid file type or file too large.');
        return;
      }
      if (accepted.length) {
        const f = accepted[0];
        setFile(f);
        setPreview(URL.createObjectURL(f));
        setError(undefined);
        setUploaded(false);
        if (autoUpload) uploadFile(f);
      }
    },
    [autoUpload]
  );

  // react-dropzone setup
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': [], 'image/png': [] },
    multiple: false,
    maxSize: 5 * 1024 * 1024,
    onDropRejected: () => setError('Invalid file type or file too large.'),
    noClick: true,
    noKeyboard: true,
  });

  // cleanup preview URL
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  return (
    <div className="uploader">
      <div {...getRootProps({ className: 'uploader--dropzone' })}>
        <input {...getInputProps()} />

        {!preview && !currentImage && (
          <div className="uploader--placeholder">
            {isDragActive ? 'Drop file here…' : 'Drop file here'}
          </div>
        )}

        <div className="uploader--actions">
          <Button onClick={open} disabled={uploading}>
            Choose File
          </Button>

          {/* show upload only if user-selected file, manual upload, and not yet uploaded */}
          {file && !autoUpload && !uploading && !uploaded && (
            <Button onClick={() => uploadFile(file)} disabled={uploading}>
              Upload
            </Button>
          )}
        </div>

        {preview ? (
          <div className="uploader--thumbnail-container">
            <button
                type="button"
                className="uploader--remove-button"
                onClick={() => {
                setFile(null);
                setPreview(null);
                setError(undefined);
                setUploaded(false);
              }}
            >×
            </button>
            <img
                className="uploader--thumbnail"
                src={preview}
                alt="Preview"
            />
            <div className="uploader--file-footer">
              <span className="uploader--filename">{file.name}</span>
              <span className="uploader--filesize">
                {(file.size / 1024).toFixed(2)} KB
              </span>
            </div>
          </div>
        ) : (
          currentImage && (
            <div className="uploader--current-image-thumbnail">
              {currentImage}
            </div>
          )
        )}
      </div>

      {error && <Error error={error} />}
    </div>
  );
}

ImageUploader.propTypes = {
  autoUpload: PropTypes.bool,
  currentImage: PropTypes.node,
  formData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};
